import {
  ButtonBase,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField
} from '@material-ui/core'
import React, { useMemo, useState } from 'react'

import { Alert } from '@material-ui/lab'
import CheckBox from '@material-ui/icons/CheckBox'
import { CheckBoxOutlineBlank } from '@material-ui/icons'
import { Spacer } from '@vestaboard/installables'
import { TruncatedId } from '../../truncated-id'
import { isUUID } from '../../../utils'
import { useGetPersonQuery } from '../../../api/useGetPersonName'
import { useListNotificationSubscribers } from '../hooks/useListNotificationSubscribers'

export interface ISubscriber {
  id: string
  title: string
}

interface ISubscriberSelector {
  disabled?: boolean
  subscribers: ISubscriber[]
  setSubscribers: (subscribers: ISubscriber[]) => void
}

interface ISearchField {
  setTerms: (terms: string) => void
}

const SearchField = (props: ISearchField) => {
  const [terms, setTerms] = useState('')

  return (
    <TextField
      label='Search'
      fullWidth
      variant='outlined'
      value={terms}
      onChange={e => setTerms(e.target.value)}
      onBlur={() => {
        props.setTerms(terms)
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault()
          props.setTerms(terms)
        }
      }}
    />
  )
}

export const SubscriberSelector = (props: ISubscriberSelector) => {
  const [terms, setTerms] = useState('')

  const { data: personData, loading: personLoading, error: personError } = useGetPersonQuery({
    variables: {
      personId: terms
    },
    skip: !isUUID(terms)
  })
  const { data, loading, error } = useListNotificationSubscribers(
    {
      input: {
        terms
      }
    },
    !terms || isUUID(terms)
  )
  const subscriberIds = useMemo(() => props.subscribers.map(subscriber => subscriber.id), [props.subscribers])

  return (
    <>
      <SearchField setTerms={setTerms} />
      <Spacer size='medium' />
      {(loading || personLoading) && <CircularProgress />}
      {(error || personError) && (
        <Alert severity='error'>
          There was an error loading the subscribers matching your term: "<strong>{terms}</strong>"
        </Alert>
      )}
      {(!!props.subscribers?.length ||
        !!data?.listNotificationSubscribers?.notificationSubscribers?.length ||
        !!personData?.person) && (
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              {props.subscribers
                .filter(subscriber =>
                  !terms
                    ? true
                    : subscriber.id === terms || subscriber.title.toLowerCase().includes(terms.toLowerCase())
                )
                .sort((a, b) => a.title.localeCompare(b.title))
                .map(subscriber => (
                  <TableRow key={subscriber.id}>
                    <TableCell>
                      <ButtonBase
                        disabled={props.disabled}
                        onClick={() => {
                          props.setSubscribers(props.subscribers.filter(record => record.id !== subscriber.id))
                        }}>
                        <CheckBox />
                      </ButtonBase>
                    </TableCell>
                    <TableCell>
                      <TruncatedId value={subscriber.id} path={`/notifications/subscriber/${subscriber.id}`} full />
                    </TableCell>
                    <TableCell>{subscriber.title}</TableCell>
                  </TableRow>
                ))}
              {personData?.person && !subscriberIds.includes(personData?.person?.id) ? (
                <TableRow key={personData.person.id}>
                  <TableCell>
                    <ButtonBase
                      disabled={props.disabled}
                      onClick={() => {
                        props.setSubscribers([
                          ...props.subscribers,
                          {
                            id: personData.person.id,
                            title: `${personData.person.firstName || ''} ${personData.person.lastName || ''}`.trim()
                          }
                        ])
                      }}>
                      <CheckBoxOutlineBlank />
                    </ButtonBase>
                  </TableCell>
                  <TableCell>
                    <TruncatedId
                      value={personData.person.id}
                      path={`/notifications/subscriber/${personData.person.id}`}
                      full
                    />
                  </TableCell>
                  <TableCell>
                    {`${personData.person.firstName || ''} ${personData.person.lastName || ''}`.trim()}
                  </TableCell>
                </TableRow>
              ) : null}
              {(data?.listNotificationSubscribers?.notificationSubscribers || [])
                .filter(subscriber => !subscriberIds.includes(subscriber.id))
                .map(subscriber => (
                  <TableRow key={subscriber.id}>
                    <TableCell>
                      <ButtonBase
                        disabled={props.disabled}
                        onClick={() => {
                          props.setSubscribers([
                            ...props.subscribers,
                            {
                              id: subscriber.id,
                              title: subscriber.title
                            }
                          ])
                        }}>
                        <CheckBoxOutlineBlank />
                      </ButtonBase>
                    </TableCell>
                    <TableCell>
                      <TruncatedId value={subscriber.id} path={`/notifications/subscriber/${subscriber.id}`} full />
                    </TableCell>
                    <TableCell>{subscriber.title}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}
